import React, { useMemo } from "react";
import { IonItem } from "@ionic/react";
import { IEvidence, ProgramData, ProgramSkill } from "@interfaces/Interfaces";
import { Button_Disclosure_Right_Grey } from "@assets/images";
import * as ProgramUtils from "@utils/programUtils";
import classNames from "classnames";
import { isAfter, isSameDay, subMonths } from "date-fns";

type Props = {
  skill: ProgramSkill;
  programEvidence: IEvidence[];
  program: ProgramData;
  count?: number;
  buttonPressed: () => void;
};

const ProgramSkillButton: React.FC<Props> = (props) => {
  const duration = useMemo(() => parseInt(props.skill.duration ?? 0, 10), [props.skill]);

  const skillEvidence = useMemo(
    () =>
      props.programEvidence?.filter((evidence: IEvidence) => {
        const evidenceJSON = evidence.evidenceJSON;
        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        if (onHoldReset === 1 || evidence.draft) {
          return false;
        }

        const evidenceSkillKey = ProgramUtils.getSkillEvidenceKey(props.program);

        if (evidenceSkillKey) {
          const evidenceSkill = evidenceJSON[evidenceSkillKey];

          return evidenceSkill?.trim() === props.skill.Name.trim();
        }

        return false;
      }),
    [props.programEvidence, props.skill, props.program]
  );

  const RAGRatingCount = useMemo(() => {
    const dateThreshold = duration > 0 ? subMonths(new Date(), duration - 1).setDate(1) : undefined;

    if (dateThreshold) {
      return skillEvidence.filter(
        (evidence) =>
          isSameDay(new Date(evidence.date), dateThreshold) || isAfter(new Date(evidence.date), dateThreshold)
      ).length;
    }

    return skillEvidence.length;
  }, [skillEvidence]);

  const count = useMemo(() => props.count ?? skillEvidence?.length ?? 0, [skillEvidence, props.count]);

  const numberRequiredInDuration = useMemo(
    () => parseInt(props.skill.numberRequiredInDuration ?? 0, 10),
    [props.skill]
  );

  const isRequired = useMemo(() => numberRequiredInDuration > 0, [numberRequiredInDuration]);

  const durationDescription =
    props.skill.durationDescription && `${props.skill.durationDescription}${duration === 1 ? "" : "s"}`;

  return (
    <IonItem
      button
      className="!flex flex-col !w-full !shadow-unset !pl-0 part program-skill-button"
      detail={false}
      key={props.skill.ID}
      onClick={props.buttonPressed}
    >
      <div className="w-full pl-0">
        <div className="flex flex-row justify-between items-center shadow-program-skill shadow-grey-30 py-[8px]">
          <div className="flex flex-col">
            <div className="text-acc-15px leading-[1.33] tracking-[-0.3px] text-grey-90">
              {props.skill.Name}
              {isRequired && ` (min. ${numberRequiredInDuration} in ${duration} ${durationDescription})`}
            </div>
            <div className="text-acc-11px font-medium leading-[1.27] text-grey-60">{`${count} evidence`}</div>
          </div>
          <div className="flex flex-row items-center flex-shrink-0 ml-4">
            {isRequired && (
              <RAGRating
                count={RAGRatingCount}
                total={numberRequiredInDuration}
              />
            )}
            <img
              className="size-6 img-no-select"
              src={Button_Disclosure_Right_Grey}
            />
          </div>
        </div>
      </div>
    </IonItem>
  );
};

export default ProgramSkillButton;

type RAGRatingProps = {
  count: number;
  total: number;
};

const RAGRating: React.FC<RAGRatingProps> = (props) => {
  const bgColor = useMemo(
    () => ({
      red: "!bg-cta-red",
      amber: "!bg-[#FEEBC8]",
      green: "!bg-[#C6F7C1]",
    }),
    []
  );

  const textColor = useMemo(
    () => ({
      red: "!text-white",
      amber: "!text-[#9C4221]",
      green: "!text-[#085333]",
    }),
    []
  );

  const getBackgroundColor = (count: number, total: number) => {
    if (count === 0) {
      return bgColor.red;
    }

    if (count < total) {
      return bgColor.amber;
    }

    return bgColor.green;
  };

  const getTextColor = (count: number, total: number) => {
    if (count === 0) {
      return textColor.red;
    }

    if (count < total) {
      return textColor.amber;
    }

    return textColor.green;
  };

  return (
    <span
      className={classNames(
        "flex items-center justify-center h-5 px-2 rounded-xl",
        getBackgroundColor(props.count, props.total)
      )}
    >
      <span
        className={classNames("text-12px font-semibold my-0.5", getTextColor(props.count, props.total))}
      >{`${props.count} / ${props.total}`}</span>
    </span>
  );
};
